import React, { useMemo, useState } from 'react';
import { buildTrace, spanBarBox, KIND_LABELS } from './auditTrace';

const kindText = {
  turn: 'text-slate-600',
  model: 'text-emerald-700',
  tool: 'text-orange-700',
  db: 'text-sky-700',
};

const kindBar = {
  turn: 'bg-slate-400',
  model: 'bg-emerald-500',
  tool: 'bg-orange-400',
  db: 'bg-sky-500',
};

function formatDuration(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.round((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function formatWhen(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

// The row is inserted when the turn finishes, so created_at is the end.
// Started is that write time minus the recorded duration.
function turnWindow(audit) {
  const ended = audit?.created_at ? new Date(audit.created_at) : null;
  if (!ended || Number.isNaN(ended.getTime())) return { started: null, ended: null };
  const duration = Number(audit.duration_ms);
  const started = Number.isFinite(duration) ? new Date(ended.getTime() - duration) : null;
  return { started, ended };
}

function JsonBlock({ value }) {
  if (value == null || value === '') return <p className="text-sm text-gray-400">None</p>;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <pre className="text-xs bg-gray-900 text-gray-100 rounded-lg p-3 overflow-auto max-h-96 whitespace-pre-wrap break-words">
      {text}
    </pre>
  );
}

function SummaryRow({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <dt className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 text-right">{children}</dd>
    </div>
  );
}

function TokenUsage({ usage }) {
  const input = Number(usage?.inputTokens) || 0;
  const output = Number(usage?.outputTokens) || 0;
  const total = input + output;
  if (!usage || total <= 0) {
    return <p className="text-sm text-gray-400">Not recorded for this turn</p>;
  }
  const inputPct = Math.round((input / total) * 100);
  const outputPct = 100 - inputPct;
  return (
    <div>
      <p className="text-lg font-semibold text-gray-900">{total.toLocaleString()}</p>
      <div className="mt-2 h-2 rounded-full overflow-hidden flex bg-gray-100">
        <div className="bg-sky-500 h-full" style={{ width: `${inputPct}%` }} />
        <div className="bg-indigo-400 h-full" style={{ width: `${outputPct}%` }} />
      </div>
      <p className="mt-1.5 text-xs text-sky-700">Input {input.toLocaleString()} ({inputPct}%)</p>
      <p className="text-xs text-indigo-700">Output {output.toLocaleString()} ({outputPct}%)</p>
      {Number(usage.cacheHitTokens) > 0 && (
        <p className="text-xs text-gray-500">Cache hit {Number(usage.cacheHitTokens).toLocaleString()}</p>
      )}
      {Number(usage.reasoningTokens) > 0 && (
        <p className="text-xs text-gray-500">Reasoning {Number(usage.reasoningTokens).toLocaleString()}</p>
      )}
    </div>
  );
}

function LatencyBar({ audit, usage }) {
  const modelMs = Number(usage?.modelMs) || 0;
  const toolMs = Number(usage?.toolMs) || 0;
  const total = Number(audit?.duration_ms) || (modelMs + toolMs);
  if (modelMs <= 0 && toolMs <= 0) {
    return (
      <div>
        <div className="h-2 rounded-full bg-slate-300" />
        <p className="mt-1.5 text-xs text-gray-500">Per-step timing was not recorded</p>
      </div>
    );
  }
  const other = Math.max(0, total - modelMs - toolMs);
  const sum = modelMs + toolMs + other || 1;
  const parts = [
    { key: 'model', label: 'Model', ms: modelMs, className: 'bg-emerald-500' },
    { key: 'tools', label: 'Tools', ms: toolMs, className: 'bg-orange-400' },
    { key: 'other', label: 'Other', ms: other, className: 'bg-slate-300' },
  ].filter((part) => part.ms > 0);
  return (
    <div>
      <div className="h-2 rounded-full overflow-hidden flex bg-gray-100">
        {parts.map((part) => (
          <div key={part.key} className={`h-full ${part.className}`} style={{ width: `${(part.ms / sum) * 100}%` }} />
        ))}
      </div>
      <div className="mt-1.5 space-y-0.5">
        {parts.map((part) => (
          <p key={part.key} className="text-xs text-gray-600">
            {part.label} {formatDuration(part.ms)}
          </p>
        ))}
      </div>
    </div>
  );
}

function RunSummary({ audit, usage }) {
  const cost = Number(usage?.costUsd);
  const window = turnWindow(audit);
  return (
    <aside className="bg-white rounded-lg shadow p-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-2">Run summary</h2>
      <dl>
        <SummaryRow label="Status">{audit?.status || '—'}</SummaryRow>
        <SummaryRow label="Model">
          <span className="block">{audit?.model || '—'}</span>
          {audit?.provider && <span className="block text-xs font-normal text-gray-500">{audit.provider}</span>}
        </SummaryRow>
        <SummaryRow label="Duration">{formatDuration(audit?.duration_ms)}</SummaryRow>
        <SummaryRow label="Started">{formatWhen(window.started)}</SummaryRow>
        <SummaryRow label="Ended">{formatWhen(window.ended)}</SummaryRow>
        <SummaryRow label="Rounds">{audit?.model_rounds_attempted ?? '—'}</SummaryRow>
        <SummaryRow label="Tool calls">{audit?.tool_calls_count ?? '—'}</SummaryRow>
        <div className="py-3 border-b border-gray-100">
          <p className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">Cost</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {cost > 0 ? `$${cost.toFixed(4)}` : '—'}
          </p>
          <p className="text-xs text-gray-500">{cost > 0 ? 'Estimated from recorded tokens' : 'Not recorded for this turn'}</p>
        </div>
        <div className="py-3 border-b border-gray-100">
          <p className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase mb-1">Token usage</p>
          <TokenUsage usage={usage} />
        </div>
        <div className="pt-3">
          <p className="text-[11px] font-semibold tracking-wide text-gray-400 uppercase mb-2">Latency</p>
          <LatencyBar audit={audit} usage={usage} />
        </div>
      </dl>
    </aside>
  );
}

function SpanRow({ span, selected, onSelect, bar, durationLabel }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(span.id)}
      className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
        selected ? 'bg-primary-50 ring-1 ring-primary-200' : 'hover:bg-gray-50'
      }`}
      style={{ paddingLeft: `${12 + span.depth * 16}px` }}
    >
      <span className={`w-14 shrink-0 text-[10px] font-bold tracking-wide ${kindText[span.kind] || kindText.tool}`}>
        {KIND_LABELS[span.kind] || 'TOOL'}
      </span>
      <span className="w-44 shrink-0 truncate font-mono text-xs text-gray-900" title={span.name}>
        {span.name}
      </span>
      <span className="relative h-2 flex-1 rounded bg-gray-100 overflow-hidden min-w-[72px]">
        <span
          className={`absolute top-0 h-full rounded ${span.success === false ? 'bg-rose-500' : (kindBar[span.kind] || kindBar.tool)}`}
          style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
        />
      </span>
      <span className={`w-16 shrink-0 text-right text-xs tabular-nums ${span.success === false ? 'text-rose-600 font-semibold' : 'text-gray-500'}`}>
        {durationLabel}
      </span>
    </button>
  );
}

function Inspector({ span }) {
  if (!span) return null;
  return (
    <div className="border-t border-gray-100 mt-3 pt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <h3 className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Input</h3>
        {span.input ? (
          <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3 max-h-80 overflow-auto">{span.input}</pre>
        ) : (
          <p className="text-sm text-gray-400">No input captured</p>
        )}
      </div>
      <div>
        <h3 className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Output</h3>
        {span.output ? (
          <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3 max-h-80 overflow-auto">{span.output}</pre>
        ) : (
          <p className="text-sm text-gray-400">No output captured</p>
        )}
      </div>
      <div className="lg:col-span-2">
        <details>
          <summary className="text-xs text-gray-500 cursor-pointer">Metadata</summary>
          <div className="mt-2">
            <JsonBlock value={span.meta} />
          </div>
        </details>
      </div>
    </div>
  );
}

function TraceView({ audit }) {
  const trace = useMemo(() => buildTrace(audit), [audit]);
  const failed = trace.spans.find((span) => span.success === false && span.kind !== 'turn');
  const auditKey = audit?.id || audit?.turn_id || '';
  const [selectedId, setSelectedId] = useState(failed?.id || 'turn');
  const [seenKey, setSeenKey] = useState(auditKey);
  if (seenKey !== auditKey) {
    setSeenKey(auditKey);
    setSelectedId(failed?.id || 'turn');
  }
  const selected = trace.spans.find((span) => span.id === selectedId) || trace.spans[0];
  const sequenced = trace.spans.filter((span) => span.kind !== 'turn');

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-4">
      <div className="bg-white rounded-lg shadow p-4 min-w-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Tool / Action Timeline</h2>
          <span className="text-xs font-medium text-gray-500">{trace.spans.length} events</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          {trace.hasTiming
            ? 'Bars are wall-clock from the start of the turn. A red bar is a step that failed.'
            : 'This turn has no per-step timing. Bars follow call order, nested under the model.'}
        </p>
        <div className="space-y-0.5">
          {trace.spans.map((span) => {
            const sequenceIndex = sequenced.findIndex((item) => item.id === span.id);
            const bar = spanBarBox(span, Math.max(sequenceIndex, 0), sequenced.length || 1, trace.hasTiming, trace.scaleMs);
            const durationLabel = span.durationMs == null ? '—' : formatDuration(span.durationMs);
            return (
              <SpanRow
                key={span.id}
                span={span}
                selected={span.id === selected?.id}
                onSelect={setSelectedId}
                bar={bar}
                durationLabel={durationLabel}
              />
            );
          })}
        </div>
        {trace.spans.length <= 1 && (
          <p className="mt-3 text-sm text-gray-500">No tool calls were captured for this turn.</p>
        )}
        <Inspector span={selected} />
      </div>
      <RunSummary audit={audit} usage={trace.usage} />
    </div>
  );
}

function UserView({ audit }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Original Prompt</h2>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{audit?.prompt || 'No prompt captured'}</p>
      </div>
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Final Assistant Response</h2>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{audit?.final_response || 'No response captured'}</p>
      </div>
    </div>
  );
}

function AiView({ audit }) {
  const trace = useMemo(() => buildTrace(audit), [audit]);
  const steps = trace.spans.filter((span) => span.kind !== 'turn');
  const selected = Array.isArray(audit?.metadata?.selectedElements) ? audit.metadata.selectedElements : [];
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">As the AI saw it</h2>
        <p className="text-sm text-gray-500">
          The full model prompt is not stored. This is the sanitized tape the turn kept: the page it was on, anything the user had selected, and each model round and tool call in order.
        </p>
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-gray-500">Page</dt>
            <dd className="font-medium text-gray-800">{audit?.current_page_path || '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Attached images</dt>
            <dd className="font-medium text-gray-800">{audit?.attached_image_count ?? 0}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Selected elements</dt>
            <dd className="font-medium text-gray-800">{selected.length || audit?.selected_element_count || 0}</dd>
          </div>
        </dl>
      </div>
      {steps.length === 0 ? (
        <p className="text-sm text-gray-500">No model or tool steps were captured.</p>
      ) : (
        steps.map((span) => (
          <div key={span.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-[10px] font-bold tracking-wide ${kindText[span.kind] || kindText.tool}`}>
                {KIND_LABELS[span.kind] || 'TOOL'}
              </span>
              <span className="font-mono text-sm text-gray-900">{span.name}</span>
              <span className={`ml-auto text-xs font-semibold ${span.success === false ? 'text-rose-600' : 'text-emerald-700'}`}>
                {span.success === false ? 'failed' : 'ok'}
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Input</h3>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3 max-h-64 overflow-auto">{span.input || 'No input captured'}</pre>
              </div>
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Output</h3>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3 max-h-64 overflow-auto">{span.output || 'No output captured'}</pre>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function MetadataView({ audit }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({
        metadata: audit?.metadata || {},
        plan_summary: audit?.plan_summary || null,
      }, null, 2));
    } catch (_) { /* clipboard can be unavailable; the JSON is still on screen */ }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Metadata</h2>
          <button type="button" onClick={copy} className="text-xs font-semibold text-primary-700 hover:underline">
            Copy JSON
          </button>
        </div>
        <JsonBlock value={audit?.metadata} />
      </div>
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Plan Summary</h2>
        <JsonBlock value={audit?.plan_summary} />
      </div>
    </div>
  );
}

const AuditTraceExplorer = ({ audit, view }) => {
  if (view === 'user') return <UserView audit={audit} />;
  if (view === 'ai') return <AiView audit={audit} />;
  if (view === 'metadata') return <MetadataView audit={audit} />;
  return <TraceView audit={audit} />;
};

export default AuditTraceExplorer;
